import { ref, watch } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'

const THEME_KEY = 'theme'

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return mode
}

function applyTheme(mode: ThemeMode) {
  const resolved = resolveTheme(mode)
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  document.documentElement.style.colorScheme = resolved
}

export function initThemeFromStorage() {
  const stored = (localStorage.getItem(THEME_KEY) as ThemeMode | null) || 'light'
  applyTheme(stored)
}

const themeMode = ref<ThemeMode>((localStorage.getItem(THEME_KEY) as ThemeMode) || 'light')

export function useTheme() {
  const setTheme = (mode: ThemeMode) => {
    themeMode.value = mode
    localStorage.setItem(THEME_KEY, mode)
    applyTheme(mode)
  }

  const toggleDark = () => {
    const next = document.documentElement.classList.contains('dark') ? 'light' : 'dark'
    setTheme(next)
  }

  watch(themeMode, (mode) => applyTheme(mode))

  if (typeof window !== 'undefined') {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (themeMode.value === 'system') applyTheme('system')
    })
  }

  return { themeMode, setTheme, toggleDark }
}
